import * as React from 'react';
import {
  IScoreboardTheme,
  IScoreboardBranding,
  IScoreboardDisplayConfig,
  DEFAULT_THEME,
  DEFAULT_BRANDING,
  DEFAULT_DISPLAY_CONFIG,
} from '../types';
import {
  useScoreboardTheme,
  UseScoreboardThemeResult,
} from '../hooks/useScoreboardTheme';
import { getFontByFamily } from '../config/fonts';
import '../styles/fonts.css';

// =============================================================================
// Context Types
// =============================================================================

export interface IScoreboardThemeContextValue {
  /** Current theme configuration */
  theme: IScoreboardTheme;
  /** Current branding configuration */
  branding: IScoreboardBranding;
  /** Current display configuration */
  display: IScoreboardDisplayConfig;
  /** Computed CSS variables and styles from useScoreboardTheme */
  computed: UseScoreboardThemeResult;
  /** Update theme (partial updates merged with current) */
  setTheme: (updates: Partial<IScoreboardTheme>) => void;
  /** Update branding (partial updates merged with current) */
  setBranding: (updates: Partial<IScoreboardBranding>) => void;
  /** Update display config (partial updates merged with current) */
  setDisplay: (updates: Partial<IScoreboardDisplayConfig>) => void;
  /** Whether settings modal is open */
  isSettingsOpen: boolean;
  /** Open settings modal */
  openSettings: () => void;
  /** Close settings modal */
  closeSettings: () => void;
}

// =============================================================================
// Context
// =============================================================================

const ScoreboardThemeContext =
  React.createContext<IScoreboardThemeContextValue | null>(null);

// =============================================================================
// Provider Props
// =============================================================================

export interface ScoreboardThemeProviderProps {
  children: React.ReactNode;
  /** Initial theme (from Firebase settings) */
  initialTheme?: Partial<IScoreboardTheme>;
  /** Initial branding (from Firebase settings) */
  initialBranding?: Partial<IScoreboardBranding>;
  /** Initial display config (from Firebase settings) */
  initialDisplay?: Partial<IScoreboardDisplayConfig>;
  /** Callback when theme changes (for persisting to Firebase) */
  onThemeChange?: (theme: IScoreboardTheme) => void;
  /** Callback when branding changes */
  onBrandingChange?: (branding: IScoreboardBranding) => void;
  /** Callback when display config changes */
  onDisplayChange?: (display: IScoreboardDisplayConfig) => void;
}

// =============================================================================
// Provider Component
// =============================================================================

/**
 * Provider that manages scoreboard theme, branding, and display state.
 *
 * Wraps the scoreboard and provides:
 * - Theme state with CSS variable computation
 * - Branding state (title, logo)
 * - Display config (items per page, show/hide options)
 * - Settings modal open/close state
 *
 * @example
 * ```tsx
 * <ScoreboardThemeProvider
 *   initialTheme={settings.theme}
 *   onThemeChange={saveScoreboardTheme}
 * >
 *   <ScoreboardContainer />
 * </ScoreboardThemeProvider>
 * ```
 */
export const ScoreboardThemeProvider: React.FC<
  ScoreboardThemeProviderProps
> = ({
  children,
  initialTheme,
  initialBranding,
  initialDisplay,
  onThemeChange,
  onBrandingChange,
  onDisplayChange,
}) => {
  // State
  const [theme, setThemeState] = React.useState<IScoreboardTheme>({
    ...DEFAULT_THEME,
    ...initialTheme,
  });

  const [branding, setBrandingState] = React.useState<IScoreboardBranding>({
    ...DEFAULT_BRANDING,
    ...initialBranding,
  });

  const [display, setDisplayState] = React.useState<IScoreboardDisplayConfig>({
    ...DEFAULT_DISPLAY_CONFIG,
    ...initialDisplay,
  });

  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);

  // Compute CSS variables from theme
  const computed = useScoreboardTheme(theme);

  // Update handlers with persistence callbacks
  const setTheme = React.useCallback(
    (updates: Partial<IScoreboardTheme>) => {
      setThemeState((prev) => {
        const next = { ...prev, ...updates };
        onThemeChange?.(next);
        return next;
      });
    },
    [onThemeChange]
  );

  const setBranding = React.useCallback(
    (updates: Partial<IScoreboardBranding>) => {
      setBrandingState((prev) => {
        const next = { ...prev, ...updates };
        onBrandingChange?.(next);
        return next;
      });
    },
    [onBrandingChange]
  );

  const setDisplay = React.useCallback(
    (updates: Partial<IScoreboardDisplayConfig>) => {
      setDisplayState((prev) => {
        const next = { ...prev, ...updates };
        onDisplayChange?.(next);
        return next;
      });
    },
    [onDisplayChange]
  );

  // Settings modal controls
  const openSettings = React.useCallback(() => setIsSettingsOpen(true), []);
  const closeSettings = React.useCallback(() => setIsSettingsOpen(false), []);

  // Sync with external changes (e.g., Firebase updates).
  // Deep compare to avoid unnecessary re-renders when parent passes new
  // object references with identical values.
  React.useEffect(() => {
    if (initialTheme) {
      setThemeState((prev) => {
        const next = { ...prev, ...initialTheme };
        return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
      });
    }
  }, [initialTheme]);

  React.useEffect(() => {
    if (initialBranding) {
      setBrandingState((prev) => {
        const next = { ...prev, ...initialBranding };
        return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
      });
    }
  }, [initialBranding]);

  React.useEffect(() => {
    if (initialDisplay) {
      setDisplayState((prev) => {
        const next = { ...prev, ...initialDisplay };
        return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
      });
    }
  }, [initialDisplay]);

  // Load Google Font dynamically when fontFamily changes
  React.useEffect(() => {
    const fontFamily = theme.fontFamily;

    // Remove existing scoreboard font link if present
    const existingLink = document.getElementById('scoreboard-google-font');
    if (existingLink) {
      existingLink.remove();
    }

    // Check if this is a Google Font (not a local font)
    if (fontFamily && fontFamily.trim()) {
      const font = getFontByFamily(fontFamily);

      // Only load Google Fonts dynamically; local fonts are already loaded via fonts.css
      if (font && font.type === 'google') {
        const link = document.createElement('link');
        link.id = 'scoreboard-google-font';
        link.rel = 'stylesheet';
        // Load multiple weights for better typography
        const fontName = encodeURIComponent(fontFamily);
        link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700;800&display=swap`;
        document.head.appendChild(link);
      }
    }

    return (): void => {
      const linkToRemove = document.getElementById('scoreboard-google-font');
      if (linkToRemove) {
        linkToRemove.remove();
      }
    };
  }, [theme.fontFamily]);

  const value: IScoreboardThemeContextValue = {
    theme,
    branding,
    display,
    computed,
    setTheme,
    setBranding,
    setDisplay,
    isSettingsOpen,
    openSettings,
    closeSettings,
  };

  return (
    <ScoreboardThemeContext.Provider value={value}>
      {children}
    </ScoreboardThemeContext.Provider>
  );
};

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to access scoreboard theme context.
 * Must be used within a ScoreboardThemeProvider.
 *
 * @throws Error if used outside of provider
 */
export const useScoreboardThemeContext = (): IScoreboardThemeContextValue => {
  const context = React.useContext(ScoreboardThemeContext);

  if (!context) {
    throw new Error(
      'useScoreboardThemeContext must be used within a ScoreboardThemeProvider'
    );
  }

  return context;
};

/**
 * Hook to access just the computed styles (convenience wrapper)
 */
export const useScoreboardStyles = (): UseScoreboardThemeResult => {
  const { computed } = useScoreboardThemeContext();
  return computed;
};
